import React, { useState } from "react";
import { IProductComposition } from "../LandingHighlighProduct";
import { useHighlightedTypographyContext } from "../../../HighlightedTypography";
import {
  _CompositionItemModalContainer,
  _CompositionItemRoot,
} from "./CompositionItem.styled";
import { ImageResponsive } from "../../../Image";
import { ImageFit } from "../../../Image/interface/ImageData";
import useBreakpoint from "../../../Grid/hooks/useBreakpoint";
import HighlightedTypography from "../../../HighlightedTypography/HighlightedTypography";
import TypographyContext from "../../../../Foundation/Typography/Typography.context";
import Modal from "../../../../Core/Modal/Modal";
import Icon from "../../../../Foundation/Icon/Icon";
import Typography, {
  ITypographyProps,
} from "../../../../Foundation/Typography/Typography";
import clsx from "clsx";

interface ICompositionItem {
  product: IProductComposition;
  theme?: "default" | "umami" | "margaux" | "petit-prince" | "adriencachot";
  className?: string;
  textColor?: ITypographyProps["color"];
}

const CompositionItem = (props: ICompositionItem) => {
  const { product, theme, className, textColor } = props;
  const isUmami = theme === "umami";
  const isMargaux = theme === "margaux";
  const isPetitPrince = theme === "petit-prince";
  const isAdriencachot = theme === "adriencachot";
  const [showDetails, setShowDetails] = useState(false);
  const { mobile } = useBreakpoint();
  const { highlightedColor: highlightedColorContext } =
    useHighlightedTypographyContext();
  const handleClick = () => {
    setShowDetails((v) => !v);
  };
  const imageId = product.imageId;
  const imageWidth = 250;
  const imageAspectRatio = 1;
  const title = product.title;
  const subtitle = product.subtitle;
  const descrition = product.description;
  const highlighted = product.highlighted;

  const itemLabel = `${product.quantity ? `${product.quantity} ` : ""}${title}`;

  const modalItemTextColor = isAdriencachot ? "white" : undefined;

  return (
    <div
      className={clsx(`p-${product.id_product} composition-item`, className, {
        "theme-margaux": isMargaux,
        "theme-umami": isUmami,
        "theme-petit-prince": isPetitPrince,
        highlighted: highlighted,
      })}
    >
      <_CompositionItemRoot
        gap={8}
        align="baseline"
        onClick={isUmami ? undefined : handleClick}
        justify={undefined}
      >
        {isPetitPrince ? (
          <Typography
            variant="body2"
            color={textColor}
            style={{
              textTransform: "uppercase",
              fontWeight: highlighted ? 800 : 400,
            }}
          >
            {itemLabel}
          </Typography>
        ) : isAdriencachot ? (
          <Typography variant="adriencachotBody2" color={textColor}>
            {itemLabel}
          </Typography>
        ) : (
          <HighlightedTypography
            variant={isMargaux && highlighted ? "margaux3" : undefined}
            color={textColor || (isUmami ? "white" : undefined)}
            highlightedText={highlighted ? itemLabel : ""}
          >
            {itemLabel}
          </HighlightedTypography>
        )}

        {!isUmami && !isMargaux && !isPetitPrince && !isAdriencachot && (
          <Icon
            icon="plus_circle"
            size={14}
            viewBox="0 0 14 14"
            color={highlightedColorContext}
          />
        )}
      </_CompositionItemRoot>
      <TypographyContext.Provider value={{ color: "" }}>
        <Modal
          type="transparent-modal"
          show={showDetails}
          closable={true}
          onCancel={() => setShowDetails(false)}
          bodyStyle={{
            maxWidth: 390,
            width: "87%",
            padding: (!mobile && "36px 48px") || undefined,
            backgroundColor: isAdriencachot ? "#000" : undefined,
          }}
          overlayStyle={{
            background: "rgba(0,0,0,0.6)",
            opacity: 1,
          }}
          content={
            <_CompositionItemModalContainer direction="column" align="center">
              <ImageResponsive
                alt=""
                desktop={{
                  image: {
                    id: imageId,
                    fit: ImageFit.COVER,
                    quality: 100,
                    aspectRatio: imageAspectRatio,
                    width: imageWidth,
                  },
                  imgStyle: {
                    overflow: "hidden",
                    aspectRatio: imageAspectRatio,
                    maxWidth: imageWidth,
                  },
                }}
                mobile={{
                  image: {
                    id: imageId,
                    fit: ImageFit.COVER,
                    quality: 80,
                    aspectRatio: imageAspectRatio,
                    outputPixelDensities: [1, 2],
                    width: imageWidth,
                  },
                  imgStyle: {
                    overflow: "hidden",
                    aspectRatio: imageAspectRatio,
                    maxWidth: imageWidth,
                  },
                }}
                style={{
                  aspectRatio: imageAspectRatio,
                  width: imageWidth,
                  marginBottom: 24,
                }}
              />
              <Typography
                variant={"ui5"}
                mb={16}
                align="center"
                color={modalItemTextColor}
              >
                {title}
              </Typography>
              <Typography
                variant={isAdriencachot ? "adriencachotUI2" : "subtitle4"}
                mb={16}
                align="center"
                color={modalItemTextColor}
              >
                {subtitle}
              </Typography>
              <Typography
                variant={isAdriencachot ? "adriencachotBody2" : "body2"}
                align="center"
                color={modalItemTextColor}
              >
                {descrition}
              </Typography>
            </_CompositionItemModalContainer>
          }
        ></Modal>
      </TypographyContext.Provider>
    </div>
  );
};

export default CompositionItem;
